import { Badge } from "@/components/ui/badge";
import {
  Table,
  TableBody,
  TableCell,
  TableFooter,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { cn } from "@/lib/utils";

import type { BilledLine } from "../api";
import { fmtCur2, fmtDate } from "../../renewals/format";

/** The QB invoice / credit-memo lines behind one account, as they appeared on
 *  the documents. Used inside the table's row expansion AND the review drawer.
 *  MRR lines are what roll into the month's MRR; a line with `is_mrr: null`
 *  never made the waterfall — worth a reviewer's eyebrow. */
export function BilledLinesTable({
  lines,
  compact = false,
}: {
  lines: BilledLine[];
  compact?: boolean;
}) {
  if (lines.length === 0) {
    return (
      <p className="px-1 py-2 text-xs text-muted-foreground">
        No QuickBooks invoice lines this month — this account's numbers come
        from the revenue waterfall only.
      </p>
    );
  }
  const total = lines.reduce((s, l) => s + l.amount, 0);
  const mrrTotal = lines.reduce((s, l) => s + (l.is_mrr ? l.amount : 0), 0);

  return (
    <Table containerClassName={compact ? "" : "max-h-80"}>
      <TableHeader>
        <TableRow>
          <TableHead>Doc #</TableHead>
          <TableHead>Date</TableHead>
          {!compact && <TableHead>Item</TableHead>}
          <TableHead>Description</TableHead>
          <TableHead align="right">Qty</TableHead>
          <TableHead align="right">Rate</TableHead>
          <TableHead align="right">Amount</TableHead>
          <TableHead align="center">MRR</TableHead>
        </TableRow>
      </TableHeader>
      <TableBody>
        {lines.map((l, i) => (
          <TableRow key={i} className={cn(l.source === "credit_memo" && "bg-danger/5")}>
            <TableCell className="whitespace-nowrap text-xs tabular-nums">
              {l.doc_number ?? "—"}
              {l.source === "credit_memo" && (
                <Badge variant="danger" className="ml-1.5">
                  Credit
                </Badge>
              )}
            </TableCell>
            <TableCell className="whitespace-nowrap text-xs">{fmtDate(l.txn_date)}</TableCell>
            {!compact && (
              <TableCell className="max-w-[220px] truncate text-xs" title={l.item_name ?? undefined}>
                {l.item_name ?? "—"}
              </TableCell>
            )}
            <TableCell
              className="max-w-[280px] truncate text-xs text-muted-foreground"
              title={l.description ?? undefined}
            >
              {l.description ?? l.item_name ?? "—"}
            </TableCell>
            <TableCell numeric className="text-xs">
              {l.quantity != null ? l.quantity.toLocaleString("en-US") : "—"}
            </TableCell>
            <TableCell numeric className="text-xs">
              {l.unit_price != null
                ? `$${l.unit_price.toLocaleString("en-US", { maximumFractionDigits: 4 })}`
                : "—"}
            </TableCell>
            <TableCell numeric className="text-xs font-medium">
              {fmtCur2(l.amount)}
            </TableCell>
            <TableCell align="center">
              {l.is_mrr === true ? (
                <Badge variant="success">MRR</Badge>
              ) : l.is_mrr === false ? (
                <span className="text-xs text-muted-foreground">—</span>
              ) : (
                <Badge
                  variant="warning"
                  title="This item didn't appear in the revenue waterfall for the month"
                >
                  Not in waterfall
                </Badge>
              )}
            </TableCell>
          </TableRow>
        ))}
      </TableBody>
      <TableFooter>
        <TableRow>
          <TableCell colSpan={compact ? 5 : 6} className="text-xs">
            {lines.length} line{lines.length === 1 ? "" : "s"} · MRR lines{" "}
            <b className="tabular-nums">{fmtCur2(mrrTotal)}</b>
          </TableCell>
          <TableCell numeric className="text-xs font-semibold">
            {fmtCur2(total)}
          </TableCell>
          <TableCell />
        </TableRow>
      </TableFooter>
    </Table>
  );
}
